import { useEffect, useState } from "react"
import ReactModal from "react-modal"
import supabase from "../supabaseconfig"
import { useBrukerContext } from "../BrukerContext"

import { Recommendation } from "../types/supabase"
import { AnbefalingModal } from "./Anbefaling"
import style from "./anbefalinger.module.css"
import Kartvisning from "./Kartvisning"
import { LeggTilAnbefalingModal } from "./LeggTilModal"
import { AnbefalingType, visningstekstForKategori } from "./utils"

export default function Anbefalinger() {
    const { bruker } = useBrukerContext()
    const [visLeggTilNyModal, setVisLeggTilNyModal] = useState(false)
    const [visFiltrering, setVisFiltrering] = useState(false)
    const [anbefalingDetaljevisning, setAnbefalingDetaljevisning] = useState<
        Recommendation | undefined
    >()
    const [recommendations, setRecommendations] = useState<Recommendation[]>([])
    const [kategorifilter, setKategorifilter] = useState<AnbefalingType[]>(
        Object.values(AnbefalingType),
    )
    const [slettPending, setSlettPending] = useState<Recommendation | undefined>()
    const [redigerAnbefaling, setRedigerAnbefaling] = useState<Recommendation | undefined>()
    const [visning, setVisning] = useState<"liste" | "kart">("liste")

    useEffect(() => {
        getRecommendations()
    }, [])

    async function slettAnbefaling(id: number): Promise<void> {
        const anbefaling = recommendations.find((r) => r.id === id)

        if (anbefaling?.photo_urls?.length) {
            const { error: storageError } = await supabase()
                .storage.from("recommendation-photos")
                .remove(anbefaling.photo_urls)

            if (storageError) {
                console.error("Error deleting photos:", storageError)
            }
        }

        const { error } = await supabase()
            .from("recommendations")
            .delete()
            .eq("id", id)

        if (error) {
            console.error("Error deleting recommendation:", error)
            return
        }

        setRecommendations((prev) => prev.filter((r) => r.id !== id))
        if (anbefalingDetaljevisning?.id === id) {
            setAnbefalingDetaljevisning(undefined)
        }
        setSlettPending(undefined)
    }

    async function getRecommendations(): Promise<void> {
        let { data, error } = await supabase()
            .from("recommendations")
            .select("*")

        if (error) {
            console.error("Error fetching recommendations:", error)
            return
        }

        if (data) {
            setRecommendations(data)
        }
    }

    return (
        <div>
            <div className="page">
                <h1 className="topHeader">ANBEFALINGER</h1>
            </div>
            <div className="underPage">
                <div className={style.anbefalingerWrapper}>
                <div className={style.toppKnapper}>
                    <button
                        className={style.filtrerKnapp}
                        type="button"
                        onClick={() => {
                            setVisFiltrering(!visFiltrering)
                        }}
                    >
                        Filtrer {visFiltrering ? "▲" : "▼"}
                    </button>
                    <div className={style.visningToggle}>
                        <button
                            className={`${style.visningKnapp} ${visning === "liste" ? style.visningAktiv : ""}`}
                            type="button"
                            onClick={() => setVisning("liste")}
                        >
                            Liste
                        </button>
                        <button
                            className={`${style.visningKnapp} ${visning === "kart" ? style.visningAktiv : ""}`}
                            type="button"
                            onClick={() => setVisning("kart")}
                        >
                            Kart
                        </button>
                    </div>
                    <button
                        className={style.leggTilKnapp}
                        type="button"
                        onClick={() => {
                            setVisLeggTilNyModal(true)
                        }}
                    >
                        + Legg til ny anbefaling
                    </button>
                </div>
                {visFiltrering && (
                    <div className={style.filterPanel}>
                        {Object.values(AnbefalingType).map((kategori) => {
                            return (
                                <label key={kategori} className={style.filterLabel}>
                                    <input
                                        onChange={(event) => {
                                            const oppdaterListe = event
                                                .target.checked
                                                ? kategorifilter.concat([
                                                      kategori,
                                                  ])
                                                : kategorifilter.filter(
                                                      (it) =>
                                                          it !== kategori,
                                                  )
                                            setKategorifilter(oppdaterListe)
                                        }}
                                        type="checkbox"
                                        checked={kategorifilter.some(
                                            (it) => kategori === it,
                                        )}
                                    />
                                    <span className={style.kategoriTag} data-type={kategori}>
                                        {visningstekstForKategori(kategori)}
                                    </span>
                                </label>
                            )
                        })}
                    </div>
                )}
                {visning === "kart" ? (
                    <>
                        <Kartvisning
                            recommendations={recommendations.filter((it) =>
                                kategorifilter.some((kategori) => kategori === it.type),
                            )}
                            onPinClick={(rec) => setAnbefalingDetaljevisning(rec)}
                        />
                        <AnbefalingModal
                            data={anbefalingDetaljevisning}
                            lukk={() => setAnbefalingDetaljevisning(undefined)}
                            vis={!!anbefalingDetaljevisning}
                            bruker={bruker}
                            forceOverlay={true}
                            onRediger={(data) => {
                                setAnbefalingDetaljevisning(undefined)
                                setRedigerAnbefaling(data)
                            }}
                        />
                    </>
                ) : (
                <div className={style.innholdRad}>
                <div className={style.layout}>
                    {recommendations
                        .filter((it) =>
                            kategorifilter.some(
                                (kategori) => kategori === it.type,
                            ),
                        )
                        .map((it: Recommendation) => (
                            <AnbefalingMinivisning
                                data={it}
                                setVisning={() =>
                                    setAnbefalingDetaljevisning(it)
                                }
                                key={it.id}
                                bruker={bruker}
                                onSlett={() => setSlettPending(it)}
                                onRediger={() => setRedigerAnbefaling(it)}
                            />
                        ))}
                    {recommendations.filter((it) =>
                        kategorifilter.some((kategori) => kategori === it.type),
                    ).length === 0 && (
                        <div className={style.ingenResultater}>
                            <p>
                                <strong>Ingen resultater</strong>
                            </p>
                        </div>
                    )}
                </div>
                <AnbefalingModal
                    data={anbefalingDetaljevisning}
                    lukk={() => setAnbefalingDetaljevisning(undefined)}
                    vis={!!anbefalingDetaljevisning}
                    bruker={bruker}
                    forceOverlay={false}
                    onRediger={(data) => {
                        setAnbefalingDetaljevisning(undefined)
                        setRedigerAnbefaling(data)
                    }}
                />
                </div>
                )}
                </div>
            </div>
            <LeggTilAnbefalingModal
                vis={visLeggTilNyModal}
                lukk={() => setVisLeggTilNyModal(false)}
                onLagret={getRecommendations}
            />
            <LeggTilAnbefalingModal
                vis={!!redigerAnbefaling}
                lukk={() => setRedigerAnbefaling(undefined)}
                eksisterende={redigerAnbefaling}
                onLagret={getRecommendations}
            />
            <ReactModal
                ariaHideApp={false}
                isOpen={!!slettPending}
                onRequestClose={() => setSlettPending(undefined)}
                style={{
                    content: {
                        inset: "auto",
                        padding: 0,
                        border: "none",
                        borderRadius: "8px",
                        maxWidth: "400px",
                        width: "fit-content",
                    },
                    overlay: {
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "rgba(0, 0, 0, 0.5)",
                    },
                }}
            >
                <div className={style.slettBekreftelse}>
                    <p>Er du sikker på at du vil slette <strong>{slettPending?.name}</strong>?</p>
                    <div className={style.knapper}>
                        <button type="button" onClick={() => setSlettPending(undefined)}>
                            Avbryt
                        </button>
                        <button
                            type="button"
                            className={style.slettBekreftKnapp}
                            onClick={() => {
                                if (slettPending?.id) {
                                    slettAnbefaling(slettPending.id)
                                }
                            }}
                        >
                            Slett
                        </button>
                    </div>
                </div>
            </ReactModal>
        </div>
    )
}

function AnbefalingMinivisning({
    data,
    setVisning,
    bruker,
    onSlett,
    onRediger,
}: {
    key: number
    data: Recommendation
    setVisning: () => void
    bruker: string
    onSlett: () => void
    onRediger: () => void
}) {
    function hentIkon(type: string): string {
        switch (type) {
            case AnbefalingType.RESTAURANT:
                return "🍽️"
            case AnbefalingType.MUSEUM:
                return "🏛️"
            case AnbefalingType.BAR:
                return "🍸"
            case AnbefalingType.AKTIVITET:
                return "🥾"
            case AnbefalingType.STRAND:
                return "🏖️"
            case AnbefalingType.ANNET:
                return "📌"
            default:
                return "❓"
        }
    }

    return (
        <div className={style.anbefaling} onClick={setVisning}>
            {data.user === bruker && (
                <div className={style.handlingsKnapper}>
                    <button
                        className={style.redigerKnapp}
                        type="button"
                        title="Rediger"
                        onClick={(e) => {
                            e.stopPropagation()
                            onRediger()
                        }}
                    >
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M11.5 1.5l3 3L5 14H2v-3L11.5 1.5z" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </button>
                    <button
                        className={style.slettKnapp}
                        type="button"
                        title="Slett"
                        onClick={(e) => {
                            e.stopPropagation()
                            onSlett()
                        }}
                    >
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M5.5 1.5h5M2 4h12M6 7v5M10 7v5M3.5 4l.75 9a1.5 1.5 0 001.5 1.5h4.5a1.5 1.5 0 001.5-1.5L12.5 4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </button>
                </div>
            )}
            <span className={style.kategoriTag} data-type={data.type}>
                {hentIkon(data.type)} {visningstekstForKategori(data.type as AnbefalingType)}
            </span>
            <div className={style.anbefalingInnholdListe}>
                <h3 className={style.anbefalingNavn}>{data.name}</h3>
                <div className={style.anbefalingDetaljer}>
                    {data.rating && <span>⭐ {data.rating}</span>}
                    {data.user && <span>Lagt til av {data.user}</span>}
                </div>
            </div>
        </div>
    )
}
